import { defaultLocale } from "@/i18n/config";
import type { EventAdminExportItem } from "@/core/services/types/event.types";
import { formatEventCalendarDate } from "@/lib/event-dates";
import { parseDateInput, toDateInput } from "@/lib/dateRange";
import {
  extractDepartmentNumber,
  resolveExportCity,
  resolveExportDepartmentName,
  resolveExportLocationLine,
} from "@/lib/placeLocation";

export type EventsLibraryExportColumn = {
  key: keyof EventsLibraryExportRow;
  label: string;
};

export type EventsLibraryExportRow = {
  name: string;
  shortDescription: string;
  placeName: string;
  city: string;
  departmentName: string;
  departmentNumber: string;
  openingDate: string;
  closingDate: string;
};

/** En-têtes fixes des fichiers exportés (CSV / TXT). */
export const EVENTS_LIBRARY_EXPORT_COLUMN_LABELS = {
  name: "Nom",
  shortDescription: "Description courte",
  placeName: "Nom du lieu",
  city: "Ville",
  departmentName: "Département",
  departmentNumber: "Numéro de département",
  openingDate: "Date d'ouverture",
  closingDate: "Date de fermeture",
} as const satisfies Record<keyof EventsLibraryExportRow, string>;

function formatExportDate(value: string | null | undefined): string {
  if (!value?.trim()) return "";
  return formatEventCalendarDate(value, defaultLocale);
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function buildEventsLibraryExportRows(
  items: EventAdminExportItem[],
): EventsLibraryExportRow[] {
  return items.map((event) => ({
    name: event.title,
    shortDescription: event.description_short?.trim() ?? "",
    placeName: event.place?.name.trim() ?? "",
    city: resolveExportCity(event.place),
    departmentName: resolveExportDepartmentName(
      event.place,
      event.department_name,
    ),
    departmentNumber: extractDepartmentNumber(event.place?.zip_code ?? null),
    openingDate: formatExportDate(event.start_date),
    closingDate: formatExportDate(event.end_date),
  }));
}

export function buildEventsLibraryExportColumns(): EventsLibraryExportColumn[] {
  return (
    Object.entries(EVENTS_LIBRARY_EXPORT_COLUMN_LABELS) as [
      keyof EventsLibraryExportRow,
      string,
    ][]
  ).map(([key, label]) => ({ key, label }));
}

export function buildEventsLibraryCsv(
  columns: EventsLibraryExportColumn[],
  rows: EventsLibraryExportRow[],
): string {
  const header = columns.map((column) => escapeCsvCell(column.label)).join(",");
  const body = rows
    .map((row) =>
      columns.map((column) => escapeCsvCell(row[column.key])).join(","),
    )
    .join("\n");

  return `\uFEFF${header}\n${body}`;
}

const TXT_LINE_BREAK = "\r\n";

/** Titre fixe de l'en-tête export TXT. */
export const EVENTS_LIBRARY_TXT_SEARCH_RECAP_TITLE = "rappel de la recherche";

function formatTxtShortDate(value: string | null | undefined): string {
  if (!value?.trim()) return "";

  const date = parseDateInput(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

function buildTxtEventBlock(event: EventAdminExportItem): string {
  const lines: string[] = [];

  const title = event.title.trim();
  if (title) lines.push(title);

  const shortDescription = event.description_short?.trim();
  if (shortDescription) lines.push(shortDescription);

  const location = resolveExportLocationLine(event.place);
  if (location) lines.push(location);

  const date = formatTxtShortDate(event.start_date);
  if (date) lines.push(date);

  return lines.join(TXT_LINE_BREAK);
}

export type EventsLibraryTxtExportOptions = {
  recapDetailLines?: string[];
};

export function buildEventsLibraryTxt(
  items: EventAdminExportItem[],
  options: EventsLibraryTxtExportOptions = {},
): string {
  const headerLines = [
    EVENTS_LIBRARY_TXT_SEARCH_RECAP_TITLE,
    ...(options.recapDetailLines ?? []).filter((line) => line.trim().length > 0),
  ];

  const blocks = items.map((event) => buildTxtEventBlock(event));

  return `${headerLines.join(TXT_LINE_BREAK)}${TXT_LINE_BREAK}${TXT_LINE_BREAK}${blocks.join(`${TXT_LINE_BREAK}${TXT_LINE_BREAK}`)}${TXT_LINE_BREAK}`;
}

export function buildEventsLibraryExportFilename(
  tab: string,
  extension: "csv" | "txt",
): string {
  const today = toDateInput(new Date());
  return `evenements-${tab.toLowerCase()}-${today}.${extension}`;
}
